import icalendar from 'icalendar';
import uuid from 'uuid/v1';
import moment from 'moment';

import {
  getCurrencyValue,
  getPricesCategoryForm,
  getPricesFixedForm as getNoDaysPricedFixedForm,
  getActivtyRatePlanPrices,
  getActivityRatePlanCheckboxStates
} from 'utils/accommodations-and-activities/prepareAccommodationAndActivityData';

import { getPrices as getAccommodationPrices } from 'utils/accommodations-and-activities/prepareReceivedAccommodationData';

import { SCHEDULE_DAYS } from 'pages/create-activity/constants';
import {
  CATEGORY_PRICES,
  DAILY_PRICES,
  FIXED_PRICES,
  FUTURE_PRICES
} from 'pages/widget/constants';

import { INTEGRATIONS_NAMES } from 'constants/base';
import { transformReceivedDeactivationDates } from './transformReceivedDeactivationDates';

export const getReceivedActivityData = data => {
  const generalForm = getGeneralInfo(data).generalForm;

  const descriptionForm = {
    overview: data.overview,
    whatToBring: data.whatToBring,
    additionalInfo: data.additionalInfo,
    cancellation: data.cancellation,
    included: data.included,
    notIncluded: data.notIncluded
  };

  const addOnsForm = {
    addOns: data.addOns.map(item => ({
      id: parseInt(item.id),
      title: item.title,
      description: item.description,
      coverPhoto: {
        image: item.photo
      },
      USD: getCurrencyValue(item.prices, 'USD'),
      EUR: getCurrencyValue(item.prices, 'EUR'),
      NOK: getCurrencyValue(item.prices, 'NOK'),
      isCreated: true
    }))
  };

  const relatedAccommodationsForm = {
    relatedAccommodations: data.relatedAccommodations.map(item => {

      const firstPrice = item.prices[0];
      const futurePrice = item?.futurePrices[0];

      const norwegianFields = {};
      if (item.titleNo) norwegianFields.title = item.titleNo;
      if (item.overviewNo) norwegianFields.overview = item.overviewNo;

      let priceType = FUTURE_PRICES || FIXED_PRICES;

      if (futurePrice?.ratePlanPrices) {
        priceType = FUTURE_PRICES;
      } else if (firstPrice?.participant?.participant) {
        priceType = CATEGORY_PRICES;
      } else if (firstPrice?.day) {
        priceType = DAILY_PRICES;
      }

      return {
        id: parseInt(item.id),
        title: item.title,
        overview: item.overview,
        Norwegian: norwegianFields,
        photo: item.photo,
        checkInAfter: moment(item.checkInTime, 'HH:mm:ss'),
        checkOutBefore: moment(item.checkOutTime, 'HH:mm:ss'),
        pricesType: priceType,
        isCreated: true,
        prices: getAccommodationPrices(item)
      };
    })
  };

  const integrationForm = {
    integrations: data.platforms.map(item => ({
      id: parseInt(item.id),
      platform: INTEGRATIONS_NAMES[item.platform] || item.platform,
      titleOnPlatform: item.titleOnPlatform,
      isCreated: true
    }))
  };

  const schedule = getSchedule(data);

  const prices = getPrices(data);

  const futurePriceForm = data.futurePriceForm;

  const secondLanguageForms = _getSecondLanguagesForm(data, addOnsForm);

  return {
    generalForm,
    descriptionForm,
    addOnsForm,
    relatedAccommodationsForm,
    integrationForm,
    futurePriceForm,
    ...schedule,
    ...prices,
    ...secondLanguageForms
  };
};

export const getGeneralInfo = data => {
  return {
    generalForm: {
      id: parseInt(data.id),
      title: data.title,
      coverPhoto: {
        image: data.photo
      },
      productCode: data.productCode,
      activityType: data.isOpen ? 'Open' : 'Private',
      minParticipants: data.minParticipants.toString(),
      maxParticipants: data.maxParticipants.toString(),
      startingPoint: data.startingPoint,
      deactivatedDates: transformReceivedDeactivationDates(
        data.activitybookingSet,
        'startingDate',
        'endingDate'
      )
    }
  };
};

export const getReceivedActivityBookingsDates = receivedAvailableParticipantsData => {
  if (!receivedAvailableParticipantsData.activities.length) {
    return receivedAvailableParticipantsData;
  }

  const receivedAvailableParticipantsPerDate =
    receivedAvailableParticipantsData.activities[0]
      .availableParticipantsPerDate;

  return {
    activities: [
      {
        availableParticipantsPerDate: receivedAvailableParticipantsPerDate.map(
          item => ({
            ...item,
            startTime:
              item.startTime &&
              moment(item.startTime, 'HH:mm:ssZZ').format('HH:mm:ss')
          })
        )
      }
    ]
  };
};

const _getSecondLanguagesForm = (data, addOnsForm) => {
  const secondLanguage = 'Norwegian';
  const secondLanguageForms = {
    [`generalForm-${secondLanguage}`]: {},
    [`descriptionForm-${secondLanguage}`]: {}
  };

  if (data.titleNo) {
    secondLanguageForms[`generalForm-${secondLanguage}`] = {
      title: data.titleNo
    };

    secondLanguageForms[`descriptionForm-${secondLanguage}`] = {
      overview: data.overviewNo,
      whatToBring: data.whatToBringNo,
      additionalInfo: data.additionalInfoNo,
      cancellation: data.cancellationNo,
      included: data.includedNo,
      notIncluded: data.notIncludedNo
    };

    addOnsForm.addOns = addOnsForm.addOns.map((item, index) => ({
      ...item,
      [secondLanguage]: {
        id: parseInt(item.id),
        title: data.addOns[index]['titleNo'],
        description: data.addOns[index]['descriptionNo']
      }
    }));
  }

  return secondLanguageForms;
};

export const getSchedule = data => {
  const scheduleProperties = icalendar.parse_calendar(data.schedule).properties;
  const scheduleComponents = icalendar.parse_calendar(data.schedule).components;

  switch (data.scheduleType) {
    case 'scheduleDateForm': {
      return {
        ['scheduleDateForm']: _getScheduleDateForm(
          scheduleProperties,
          scheduleComponents
        )
      };
    }
    case 'scheduleDateTimeForm': {
      return {
        ['scheduleDateTimeForm']: _getScheduleDateTimeForm(
          scheduleProperties,
          scheduleComponents
        )
      };
    }
    default: {
      return {
        ['schedulePassForm']: _getSchedulePassForm(scheduleProperties)
      };
    }
  }
};

const _getScheduleDateForm = (scheduleProperties, scheduleComponents) => {
  if (!scheduleProperties || !scheduleComponents || !scheduleComponents.VEVENT)
    return {};

  return {
    availability: {
      startDate:
        scheduleProperties.START_DATE[0].value === 'None'
          ? null
          : moment(scheduleProperties.START_DATE[0].value, 'YYYY-MM-DD'),
      endDate:
        scheduleProperties.END_DATE[0].value === 'None'
          ? null
          : moment(scheduleProperties.END_DATE[0].value, 'YYYY-MM-DD')
    },
    days: {
      ...SCHEDULE_DAYS,
      ...scheduleComponents.VEVENT[0].properties.DAYS.reduce(
        (a, b) => ((a[b.value] = true), a),
        {}
      )
    },
    cutoffPeriod: scheduleProperties.CUTOFF_PERIOD[0].value,
    cutoffPeriodTimeType: scheduleProperties.CUTOFF_PERIOD_TIME_TYPE[0].value
  };
};

const _getScheduleDateTimeForm = (scheduleProperties, scheduleComponents) => {
  if (!scheduleProperties || !scheduleComponents || !scheduleComponents.VEVENT)
    return {};

  return {
    availability: {
      startDate:
        scheduleProperties.START_DATE[0].value === 'None'
          ? null
          : moment(scheduleProperties.START_DATE[0].value, 'YYYY-MM-DD'),
      endDate:
        scheduleProperties.END_DATE[0].value === 'None'
          ? null
          : moment(scheduleProperties.END_DATE[0].value, 'YYYY-MM-DD')
    },
    daysArray: scheduleComponents.VEVENT.map(item => ({
      id: uuid(),
      days: {
        ...SCHEDULE_DAYS,
        ...item.properties.DAYS.reduce((a, b) => ((a[b.value] = true), a), {})
      },
      startTimeAndDuration: _getStartTimeAndDuration(item.properties)
    })),
    cutoffPeriod: scheduleProperties.CUTOFF_PERIOD[0].value,
    cutoffPeriodTimeType: scheduleProperties.CUTOFF_PERIOD_TIME_TYPE[0].value
  };
};

const _getSchedulePassForm = scheduleProperties => {
  if (!scheduleProperties) return {};

  return {
    availability: {
      startDate:
        scheduleProperties.START_DATE[0].value === 'None'
          ? null
          : moment(scheduleProperties.START_DATE[0].value, 'YYYY-MM-DD'),
      endDate:
        scheduleProperties.END_DATE[0].value === 'None'
          ? null
          : moment(scheduleProperties.END_DATE[0].value, 'YYYY-MM-DD')
    }
  };
};

export const getPrices = data => {
  switch (data.scheduleType) {
    case 'schedulePassForm': {
      return {
        ['pricesFixedForm']: getNoDaysPricedFixedForm(data),
        ['pricesEqualForm']: getPricesCategoryForm(data)
      };
    }
    default: {
      return {
        ['pricesFixedForm']: getPricesFixedForm(data),
        ['pricesDailyForm']: _getPricesDailyForm(data),
        ['pricesDailyScheduleForm']: _getPricesDailyScheduleForm(data),
        ['pricesEqualForm']: getPricesCategoryForm(data),
        ['activityFuturePricesForm']: getActivityRatePlanCheckboxStates(data),  // Checkbox format for form
        ['activityFuturePricesFormDisplay']: getActivtyRatePlanPrices(data)     // Array format for display
      };
    }
  }
};

const _getPricesDailyForm = data => {
  const pricesDays = _getUniquePricesDays(data.prices);

  return {
    days: pricesDays.map(day => {
      const dayPrices = data.prices.filter(price => {
        if (price.days) {
          const priceDays = icalendar
            .parse_calendar(price.days)
            .components.VEVENT[0].properties.DAYS.map(item => item.value);

          if (priceDays.includes(day)) return true;
        }
      });

      const categoriesPrices = {};

      dayPrices.map(item => {
        categoriesPrices[item.participant.participant || 'Adult'] = {
          ...categoriesPrices[item.participant.participant || 'Adult'],
          [item.currency.currency]: item.value.toString()
        };
      });

      return {
        id: uuid(),
        day: day,
        categories: Object.keys(categoriesPrices).map(field => ({
          participant: field,
          USD: categoriesPrices[field]['USD'] || '0',
          EUR: categoriesPrices[field]['EUR'] || '0',
          NOK: categoriesPrices[field]['NOK'] || '0'
        }))
      };
    })
  };
};

const _getPricesDailyScheduleForm = data => {
  const multiplePriceDays = _getUniqueMultiplePricesDays(data.prices);

  return {
    daysArray: multiplePriceDays.map(day => {
      const dayPrices = data.prices.filter(price => {
        if (price.days) {
          const pricesDays = icalendar
            .parse_calendar(price.days)
            .components.VEVENT.map(item =>
              item.properties.DAYS.map(day => day.value)
            );

          for (let i in pricesDays) {
            if (pricesDays.hasOwnProperty(i)) {
              if (_isSameArrays(pricesDays[i], day)) return true;
            }
          }
        }
      });

      const categoriesPrices = {};

      dayPrices.map(item => {
        categoriesPrices[item.participant.participant || 'Adult'] = {
          ...categoriesPrices[item.participant.participant || 'Adult'],
          [item.currency.currency]: item.value.toString()
        };
      });

      return {
        id: uuid(),
        days: day,
        categories: Object.keys(categoriesPrices).map(field => ({
          participant: field,
          USD: categoriesPrices[field]['USD'] || '0',
          EUR: categoriesPrices[field]['EUR'] || '0',
          NOK: categoriesPrices[field]['NOK'] || '0'
        }))
      };
    })
  };
};

const getPricesFixedForm = data => {
  const pricesDays = _getUniqueMultiplePricesDays(data.prices);

  return {
    days: pricesDays.map(day => {
      const dayPrices = data.prices.filter(price => {
        if (price.days) {
          const pricesDays = icalendar
            .parse_calendar(price.days)
            .components.VEVENT.map(item =>
              item.properties.DAYS.map(day => day.value)
            );

          for (let i in pricesDays) {
            if (pricesDays.hasOwnProperty(i)) {
              if (_isSameArrays(pricesDays[i], day)) return true;
            }
          }
        }
      });

      const categoriesPrices = {};

      dayPrices.map(item => {
        categoriesPrices[item.participant.participant || 'Adult'] = {
          ...categoriesPrices[item.participant.participant || 'Adult'],
          [item.currency.currency]: item.value.toString()
        };
      });

      const categories =
        Object.keys(categoriesPrices).map(field => ({
          participant: field,
          USD: categoriesPrices[field]['USD'] || '0',
          EUR: categoriesPrices[field]['EUR'] || '0',
          NOK: categoriesPrices[field]['NOK'] || '0'
        })) || [];

      return {
        id: uuid(),
        days: day,
        USD: (categories.length && categories[0]['USD']) || '0',
        EUR: (categories.length && categories[0]['EUR']) || '0',
        NOK: (categories.length && categories[0]['NOK']) || '0'
      };
    })
  };
};

const _getStartTimeAndDuration = properties => {
  if (!properties || !properties.START_TIME) return [];
  return properties.START_TIME.map((item, index) => ({
    startTime: moment(item.value, 'HH:mm:ss'),
    duration: parseInt(properties.DURATION_[index].value),
    timeType: properties.TIME_TYPE[index].value
  }));
};

const _getUniquePricesDays = prices => {
  const daysArray = prices
    .map(item => {
      if (item.days) {
        return icalendar
          .parse_calendar(item.days)
          .components.VEVENT[0].properties.DAYS.map(day => day.value);
      }
      return '';
    })
    .filter(item => item.length);

  const uniqueDaysArray = Array.from(
    new Set(daysArray.map(JSON.stringify)),
    JSON.parse
  );

  let days = [];

  uniqueDaysArray.map(item => (days = [...days, ...item]));

  return [...new Set(days)];
};

const _getUniqueMultiplePricesDays = prices => {
  const daysArray = prices
    .map(item => {
      if (item.days) {
        return icalendar
          .parse_calendar(item.days)
          .components.VEVENT.map(item =>
            item.properties.DAYS.map(day => day.value)
          );
      }
      return '';
    })
    .filter(item => item.length);

  const uniqueDaysArray = Array.from(
    new Set(daysArray.map(JSON.stringify)),
    JSON.parse
  );

  let days = [];

  uniqueDaysArray.map(item => (days = [...days, ...item]));

  return [...new Set(days)];
};

const _isSameArrays = (array1, array2) => {
  return (
    array1.length === array2.length &&
    array1.every((element, index) => element === array2[index])
  );
};
